import React from "react";
import styled from "styled-components";

/* ────────────────────── Типы ────────────────────── */

interface ChangelogField {
  field: string;
  type: "new" | "fixed" | "changed" | "deprecated";
  description: string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  summary: string;
  sections: {
    label: string;
    type: "new" | "fix" | "improvement" | "breaking";
    items: string[];
    fields?: ChangelogField[];
  }[];
}

/* ────────────────────── Данные ────────────────────── */

const changelog: ChangelogEntry[] = [
  {
    version: "1.2.0",
    date: "08.05.2026",
    title: "Гео-маршрутизация трансгран-реквизитов",
    summary:
      "В POST /api/v1/orders добавлено опциональное поле requisiteCountry для фильтрации реквизита по стране при трансгран-методах (CROSSBORDER_*). Если задан — система подбирает реквизит из банков указанной страны соответствующего типа метода. Если не передан — фильтрация по стране не применяется. На момент введения поддерживаются: AM, TJ, UZ, AB.",
    sections: [
      {
        label: "Новые поля",
        type: "new",
        items: [
          "requisiteCountry — опциональное поле в теле POST /api/v1/orders для гео-маршрутизации реквизита",
          "Применяется только к трансгран-методам (CROSSBORDER_PHONE, CROSSBORDER_CARD и др.)",
          "Формат — ISO 3166-1 alpha-2 (например: AM, TJ, UZ, AB)",
        ],
        fields: [
          {
            field: "requisiteCountry",
            type: "new",
            description:
              "string | optional — ISO 3166-1 alpha-2 код страны для гео-фильтра реквизита. Действует только для CROSSBORDER_* методов. Поддержано: AM, TJ, UZ, AB. Без поля — гео-фильтр не применяется.",
          },
        ],
      },
      {
        label: "Поведение при подборе реквизита",
        type: "improvement",
        items: [
          "method=CROSSBORDER_PHONE + requisiteCountry=TJ → SBP-реквизит любого таджикского банка",
          "method=CROSSBORDER_CARD + requisiteCountry=AM → C2C-реквизит любого армянского банка",
          "Список поддерживаемых стран расширяется по мере подключения банков",
        ],
      },
    ],
  },
  {
    version: "1.1.0",
    date: "12.04.2026",
    title: "Полные реквизиты в ответе API",
    summary:
      "Объект paymentDetails теперь содержит все данные, необходимые для отображения платёжной формы клиенту: номер телефона, номер карты, ФИО получателя. Исправлена проблема с отсутствием банка в ответе.",
    sections: [
      {
        label: "Новые поля",
        type: "new",
        items: [
          "paymentDetails.phoneNumber — номер телефона для СБП-переводов",
          "paymentDetails.cardNumber — полный номер карты получателя (ранее отдавался только маскированный)",
          "paymentDetails.recipientFio — ФИО получателя платежа",
        ],
        fields: [
          {
            field: "paymentDetails.phoneNumber",
            type: "new",
            description: "string | null — Телефон СБП или телефон держателя",
          },
          {
            field: "paymentDetails.cardNumber",
            type: "new",
            description: "string | null — Полный номер карты (для метода CARD_NUMBER)",
          },
          {
            field: "paymentDetails.recipientFio",
            type: "new",
            description: "string | null — ФИО получателя платежа",
          },
        ],
      },
      {
        label: "Исправления",
        type: "fix",
        items: [
          "paymentDetails.bankName — мог возвращать null, даже когда банк привязан к реквизиту. Теперь корректно определяется через fallback-цепочку",
          "paymentDetails.bankCode — аналогичное исправление",
          "order.requisite.holder.bank — при отсутствии банка у держателя теперь корректно подставляется банк из реквизита",
        ],
        fields: [
          {
            field: "paymentDetails.bankName",
            type: "fixed",
            description: "Исправлен fallback: holder.bank → requisite.bank",
          },
          {
            field: "paymentDetails.bankCode",
            type: "fixed",
            description: "Исправлен fallback: holder.bank → requisite.bank",
          },
        ],
      },
      {
        label: "Рекомендации",
        type: "improvement",
        items: [
          "Для отображения реквизитов клиенту используйте поля из paymentDetails — они содержат полные и корректные данные",
          "Поле order.requisite доступно для обратной совместимости, но paymentDetails является предпочтительным источником",
        ],
      },
    ],
  },
  {
    version: "1.0.0",
    date: "09.04.2026",
    title: "Начальная версия API",
    summary: "Базовый функционал платёжного агрегатора.",
    sections: [
      {
        label: "Функционал",
        type: "new",
        items: [
          "Создание ордеров (POST /orders)",
          "Получение статуса ордера по invId",
          "Каскадная система подбора трейдеров",
          "Поддержка методов оплаты: SBP, CARD_NUMBER",
          "Webhook-уведомления об изменении статуса",
          "Платёжные сессии и платёжная страница",
        ],
      },
    ],
  },
];

/* ────────────────────── Стили ────────────────────── */

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 900px;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const EntryCard = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  overflow: hidden;
`;

const EntryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const VersionBadge = styled.span`
  background: var(--accent-primary);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  letter-spacing: 0.02em;
`;

const EntryTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  min-width: 200px;
`;

const EntryDate = styled.span`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const EntryBody = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Summary = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const sectionColors: Record<string, { bg: string; text: string; border: string }> = {
  new: { bg: "rgba(34,197,94,0.08)", text: "#16a34a", border: "rgba(34,197,94,0.25)" },
  fix: { bg: "rgba(239,68,68,0.08)", text: "#dc2626", border: "rgba(239,68,68,0.25)" },
  improvement: { bg: "rgba(59,130,246,0.08)", text: "#2563eb", border: "rgba(59,130,246,0.25)" },
  breaking: { bg: "rgba(245,158,11,0.08)", text: "#d97706", border: "rgba(245,158,11,0.25)" },
};

const SectionLabel = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "sectionType",
})<{ sectionType: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  width: fit-content;
  background: ${(p) => sectionColors[p.sectionType]?.bg || sectionColors.new.bg};
  color: ${(p) => sectionColors[p.sectionType]?.text || sectionColors.new.text};
  border: 1px solid ${(p) => sectionColors[p.sectionType]?.border || sectionColors.new.border};
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const Item = styled.li`
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
  padding-left: 1.25rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent-primary);
  }
`;

const FieldsTable = styled.div`
  margin-top: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  overflow: hidden;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1rem;
  padding: 0.625rem 1rem;
  font-size: 0.8rem;
  border-bottom: 1px solid var(--border-color);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
`;

const FieldName = styled.code`
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: 0.8rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const FieldDesc = styled.span`
  color: var(--text-secondary);
`;

const fieldBadgeColors: Record<string, string> = {
  new: "#16a34a",
  fixed: "#dc2626",
  changed: "#2563eb",
  deprecated: "#d97706",
};

const FieldBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "fieldType",
})<{ fieldType: string }>`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  color: ${(p) => fieldBadgeColors[p.fieldType] || "#888"};
  margin-left: 0.5rem;
`;

const sectionIcons: Record<string, string> = {
  new: "+",
  fix: "~",
  improvement: "i",
  breaking: "!",
};

/* ────────────────────── Компонент ────────────────────── */

const Changelog: React.FC = () => {
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Changelog API</PageTitle>
      </PageHeader>

      {changelog.map((entry) => (
        <EntryCard key={entry.version}>
          <EntryHeader>
            <VersionBadge>v{entry.version}</VersionBadge>
            <EntryTitle>{entry.title}</EntryTitle>
            <EntryDate>{entry.date}</EntryDate>
          </EntryHeader>
          <EntryBody>
            <Summary>{entry.summary}</Summary>

            {entry.sections.map((section, idx) => (
              <Section key={idx}>
                <SectionLabel sectionType={section.type}>
                  {sectionIcons[section.type]} {section.label}
                </SectionLabel>
                <ItemList>
                  {section.items.map((item, i) => (
                    <Item key={i}>{item}</Item>
                  ))}
                </ItemList>
                {section.fields && section.fields.length > 0 && (
                  <FieldsTable>
                    {section.fields.map((f, i) => (
                      <FieldRow key={i}>
                        <div>
                          <FieldName>{f.field}</FieldName>
                          <FieldBadge fieldType={f.type}>{f.type}</FieldBadge>
                        </div>
                        <FieldDesc>{f.description}</FieldDesc>
                      </FieldRow>
                    ))}
                  </FieldsTable>
                )}
              </Section>
            ))}
          </EntryBody>
        </EntryCard>
      ))}
    </PageContainer>
  );
};

export default Changelog;
